import { Row } from "antd"
import cx from "clsx"
import { Button, Card, Divider, Icon, Text, Tooltip } from "components/common"
import ThemeConfig from "configs/theme"
import WhatsappCallButton from "features/clients/components/whatsapp-call-button"
import { t } from "i18next"
import { openMailTo } from "utils"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES, PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import getPrivilege from "utils/privileges/privileges"
import styles from "./clientContactCard.module.less"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { TitleDescription } from "components/custom"
import CallButton from "features/clients/components/call-button"
import GrandStreamCallButton from "features/clients/components/grandStreamCallButton"
import { configs } from "configs"
import SalesPlaybookDrawer from "features/leads/components/salesPlaybook"
const { variables: theme } = ThemeConfig

const ClientContactCard = ({ data, whatsAppChatRef }) => {
  const { t } = useTranslation()
  const [activeSlide, setActiveSlide] = useState(0)
  const salesPlayBookDrawerRef = useRef()
  const slides = useMemo(() => {
    return [
      {
        id: "call",
        icon: "MdPhone",
        title: t("Best time to Call"),
        timeRange: data?.bestCallHour,
        show: !!data?.bestCallHour,
      },
      // {
      //   id: "message",
      //   icon: "BiMessageDetail",
      //   title: t("Best time to Message"),
      //   timeRange: client?.bestMessageHour || client?.bestCallHour, // Use call time as fallback for now
      //   description: t("Most effective time to message according to AI."),
      //   show: !!client?.bestMessageHour || !!client?.bestCallHour, // Show if we have message or call data
      // },
    ].filter(slide => slide.show)
  }, [data])

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    if (slides?.length <= 1) return

    const interval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % slides?.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [slides.length])

  if (!slides.length) return null

  const hasMultipleSlides = slides.length > 1
  return (
    <>
      <Card className={styles.callCard}>
        <Row style={{ gap: 10, marginBottom: 15 }}>
          <Button
            icon="HiOutlineMail"
            type="light"
            color={theme["primary-color"]}
            shape="circle"
            content={t("Email")}
            style={{ padding: "16px" }}
            disabled={!getPrivilege("can_send_email", data?.privileges)}
            onClick={() => {
              fireEvent({
                ...ACTION_TYPES.email_contact_client,
                user_input: data?.email,
                client_id: data?.id,
              })
              openMailTo(data?.email)
            }}
          />
          <WhatsappCallButton
            client={data}
            from={{ interacted_from: PAGE_SUBSECTIONS.client_detail_page }}
            disabled={!getPrivilege("can_view_client_contact", data?.privileges)}
            shape="circle"
            style={{ padding: "16px" }}
          />
          <CallButton
            client={data}
            from={{ interacted_from: PAGE_SUBSECTIONS.client_detail_page }}
            className={styles.callBtnTEXT}
            disabled={!getPrivilege("can_view_client_contact", data?.privileges)}
            shape="circle"
            style={{ padding: "16px" }}
          />
          {!!getPrivilege("can_search_clients", data?.privileges) && (
            <Button
              icon="BsStars"
              type="light"
              color={theme["accent-color"]}
              shape="circle"
              content={t("AI Sales Playbook")}
              style={{ padding: "16px" }}
              onClick={() => {
                salesPlayBookDrawerRef.current?.openDrawer({ client: data })
              }}
            />
          )}
          {configs?.allowGrandStreamCall && (
            <GrandStreamCallButton
              client={data}
              from={{
                page: PAGE_SUBSECTIONS.client_detail_page,
                interacted_from: PAGE_SUBSECTIONS.client_detail_page,
              }}
              className={styles.callBtnTEXT}
              disabled={!getPrivilege("can_view_client_contact", data?.privileges)}
              shape="circle"
              style={{ padding: "16px" }}
            />
          )}
          {getPrivilege("can_view_whatsapp_chat_logs", data?.privileges) && (
            <>
              <div className={styles.buttonDivider} />
              <Button
                icon={"RiWhatsappFill"}
                iconProps={{ size: "1.6em" }}
                onClick={e => {
                  whatsAppChatRef?.current?.setVisible()
                }}
                text={
                  <Tooltip title={t("View Chats")}>
                    <span>{t("Chats")}</span>
                  </Tooltip>
                }
                color={theme["primary-color"]}
                type="light"
                shape="round"
              />
            </>
          )}
        </Row>
        <Divider style={{ marginBlock: "8px" }} />
        <div
          className={cx(styles.carouselContainer, {
            [styles.singleSlide]: !hasMultipleSlides,
          })}
        >
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={cx(styles.slideContent, {
                [styles.active]: hasMultipleSlides ? index === activeSlide : true,
                [styles.static]: !hasMultipleSlides,
              })}
            >
              <div className={styles.slideWrapper}>
                <Icon icon={slide.icon} size={slide.iconSize} color={theme["accent-color"]} />
                <div className={styles.contentWrapper}>
                  <TitleDescription
                    title={
                      <>
                        <Text strong>{slide.title}:</Text>{" "}
                        <Text strong className={styles.gradientColor}>
                          <span dir="ltr">{slide.timeRange}</span>
                        </Text>
                      </>
                    }
                    description={slide.description}
                    gapSpace={2}
                  />
                </div>
                {hasMultipleSlides && (
                  <div className={styles.indicators}>
                    {slides.map((_, idx) => (
                      <span
                        key={idx}
                        className={cx(styles.indicator, {
                          [styles.activeIndicator]: idx === activeSlide,
                        })}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <SalesPlaybookDrawer refs={salesPlayBookDrawerRef} />
    </>
  )
}
export default ClientContactCard
